import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

/**
 * The drafts area's one typed-confirmation dialog: a warning, the exact
 * phrase to type, and a confirm button that stays disabled until the typed
 * text matches character for character.
 *
 * It was lifted verbatim out of `TrashedAutosavesManager`'s inline
 * "Delete All Trashed Drafts" dialog so the storage-full eviction path in
 * `AutosaveContext` could reuse it rather than grow a second implementation.
 * Both callers destroy trashed drafts that still hold everything the
 * caseworker wrote, so both get the same guard — see
 * `SPURIOUS-dir-dot-md-archive/cra/src/cpts/Form/Autosave/_DIR.md` (directory-summary framework
 * retired 2026-09-12, content archived not deleted).
 *
 * `AutosaveConfirmDialog` remains the plain yes/no dialog; use this one only
 * where the action destroys content for good.
 */
export type AutosaveTypedConfirmDialogProps = {
  open: boolean;
  title: string;
  /** Plain-language warning. Say what is destroyed and that it cannot be undone. */
  warning: React.ReactNode;
  /** Extra body shown under the warning (e.g. a storage-usage summary). */
  details?: React.ReactNode;
  /** The exact phrase the caseworker must type. */
  confirmationRequired: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Blocks the confirm button while an in-flight action runs. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const AutosaveTypedConfirmDialog: React.FC<
  AutosaveTypedConfirmDialogProps
> = ({
  open,
  title,
  warning,
  details,
  confirmationRequired,
  confirmLabel,
  cancelLabel,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const [confirmationText, setConfirmationText] = useState('');

  // A fresh prompt must never arrive pre-satisfied by what was typed into the
  // previous one.
  useEffect(() => {
    if (!open) setConfirmationText('');
  }, [open]);

  const isConfirmationValid = confirmationText === confirmationRequired;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">{warning}</Alert>

          {details}

          <Typography variant="body2">
            To confirm, type exactly:
            <Box
              component="code"
              sx={{
                display: 'block',
                mt: 1,
                p: 1,
                backgroundColor: 'grey.100',
                borderRadius: 1,
                fontSize: '0.875rem',
                fontFamily: 'monospace',
              }}
            >
              {confirmationRequired}
            </Box>
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type to confirm"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            error={confirmationText !== '' && !isConfirmationValid}
            helperText={
              confirmationText !== '' && !isConfirmationValid
                ? 'Text does not match exactly'
                : ''
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={!isConfirmationValid || busy}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutosaveTypedConfirmDialog;
